import styled from 'styled-components'
import { images } from 'images'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'store'
import Button from 'Components/Button'
import { theme } from 'styles/theme'
import Text from 'Components/Text'
import { useSelector } from 'react-redux'
import { ActionType, Selectors } from 'Reducers'
import Loading from 'Components/Loading.component'
import { formatPriceString, getFormattedPackage } from 'Lib/PaymentUtils'
import invariant from 'invariant'
import { useHistory } from 'react-router-dom'
import useBlockBackNavigation from 'Hooks/useBlockBackNavigation'
import { Trans, useTranslation } from 'react-i18next'
import { getFormattedWeight, mapRefundPolicyLink } from 'Lib'
import { getInitialSearchPath, removeLiveUrlParams } from 'Lib/AppUtils'

const Center = styled.div`
  display: flex;
  justify-content: center;
`

const MaxWidth = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  max-width: 400px;
  width: 100%;
  padding: ${({ theme }) => theme.responsiveSpacing.small};
  overflow: hidden;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  justify-self: center;
  height: 26px;
  min-width: 256px;
  padding: ${({ theme }) => theme.responsiveSpacing.small};
  gap: ${({ theme }) => theme.responsiveSpacing.xsmall};
  border-radius: 64px;
  background: ${({ theme }) => theme.colors.green50};
`

const HeaderLabelPillText = styled.div`
  font-size: ${({ theme }) => theme.responsiveFontSizes.small};
  font-weight: 600;
  font-style: normal;
  color: ${({ theme }) => theme.colors.white};
`

const HeaderTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-self: center;
  margin-top: ${({ theme }) => theme.responsiveSpacing.mediumLarge};
`

const SubHeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  justify-self: center;
`

const EbookListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: ${({ theme }) => `${theme.responsiveSpacing.large} ${theme.responsiveSpacing.medium} 0`};
`

const EbookListItem = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  margin: ${({ theme }) => theme.responsiveSpacing.mediumSmall} 0;
`

const RoundImage = styled.img`
  display: block;
  width: min(60px, 15vw);
  height: min(60px, 15vw);
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid ${({ theme }) => theme.colors.green500};
  outline: 4px solid ${({ theme }) => theme.colors.green100};
`

const EbookListDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${({ theme }) => theme.responsiveSpacing.mediumSmall};
`

const LabelContainer = styled.div<{ backgroundColor?: string, fontSize?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.responsiveSpacing.xsmall};
  padding: ${({ theme }) => `${theme.responsiveSpacing.xsmall} ${theme.responsiveSpacing.small}`};
  border-radius: 24px;
  background: ${({ theme, backgroundColor }) => backgroundColor ?? theme.colors.yellow600};
  font-size: ${({ theme, fontSize }) => fontSize ?? theme.responsiveFontSizes.xSmall};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  width: fit-content;
`

const BonusLabel = styled(LabelContainer)`
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translate(-50%, -50%);
  margin-right: 0;
`

const DetailsTitle = styled.span`
  display: flex;
  font-size: ${({ theme }) => theme.fixedFontSizes.medium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.green500};
`

const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  padding: ${({ theme }) => theme.responsiveSpacing.medium};
  margin-top: ${({ theme }) => theme.responsiveSpacing.mediumLarge};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.grey50};
  box-sizing: border-box;
`

const TotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: center;
`

const TotalText = styled.span`
  font-size: ${({ theme }) => theme.responsiveFontSizes.large};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey900};
  align-self: end;
`

const TodayText = styled.span`
  font-size: ${({ theme }) => theme.responsiveFontSizes.small};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey400};
  margin-left: ${({ theme }) => theme.responsiveSpacing.xsmall};
  line-height: ${({ theme }) => theme.responsiveFontSizes.large}; // Same as TotalText
  align-self: end;
`

const RegularPriceText = styled.span`
  font-size: ${({ theme }) => theme.responsiveFontSizes.small};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey400};
  text-decoration: line-through;
  text-decoration-thickness: 1px;
`

const TotalDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const TotalDetailsAmountContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: fit-content;
`

const BulletList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 1.5em;
  list-style-type: disc;

  li {
    list-style-type: disc;
    margin-top: ${({ theme }) => theme.responsiveSpacing.small};
    font-size: ${({ theme }) => theme.responsiveFontSizes.mediumSmall};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.grey600};
  }
`

const DisclaimerText = styled.span`
  font-size: ${({ theme }) => theme.fixedFontSizes.small};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey700};
  text-align: center;
  margin: ${({ theme }) => `0 ${theme.responsiveSpacing.mediumSmall}`};
`

const EbookPurchaseContainer = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()

  const user = useSelector(Selectors.getUser)
  const products = useSelector(Selectors.getProducts)
  const subscriptionDetails = useSelector(Selectors.getSubscriptionDetails)

  // The MoR return resolves exactly once per page load. useDispatch/useHistory hand back
  // fresh references on every render, so a dep-array guard would re-fire this on each
  // re-render — and the history.replace below causes one.
  const morPurchaseResolved = useRef(false)

  useEffect(() => {
    if (!products.ebookV1) {
      dispatch({ type: ActionType.LOAD_PRODUCTS, groupId: 'ebookV1' })
    }
  }, [products])

  useEffect(() => {
    // After reloading the page, subscriptionDetails is becoming null. We need to fetch it again if it doesn't exist
    if (!subscriptionDetails) {
      dispatch({ type: ActionType.FETCH_SUBSCRIPTION_DETAILS })
    }
  }, [subscriptionDetails])

  useEffect(() => {
    // Return from a MoR (Managed Payments) hosted checkout that names this page as its
    // success_url. Wait for subscriptionDetails so the step commit saga has what it
    // needs, then move the flow forward.
    // Read from this page load's query string: sanitizeUrl has already removed the id
    // from the live URL by now, and the persisted store would resurrect it on a later
    // visit and commit the step again.
    const morSessionId = new URLSearchParams(getInitialSearchPath()).get('ebook_session_id')

    // getInitialSearchPath is a frozen snapshot of this page load, so the id stays
    // readable no matter how often this runs — the ref is what makes it fire once.
    if (!morSessionId || !subscriptionDetails || morPurchaseResolved.current) {
      return
    }

    morPurchaseResolved.current = true

    // Only the session id goes — the marketing params travelled through checkout to
    // get here, so they stay in the URL for the attribution row the saga writes.
    history.replace({ pathname: window.location.pathname, search: removeLiveUrlParams(['ebook_session_id']) })
    dispatch({ type: ActionType.RESOLVE_MOR_EBOOK_PURCHASE, checkoutSessionId: morSessionId })
  }, [subscriptionDetails])

  useBlockBackNavigation()

  if (!products.ebookV1 || !subscriptionDetails) {
    return <Loading />
  }

  const formattedPrice = getFormattedPackage(products, 'ebookV1', undefined, true)

  invariant(formattedPrice, 'formattedPrice must exist')

  const ebookPrice = products.ebookV1[0] // ebook has only one price

  const purchaseEbook = () => {
    dispatch({ type: ActionType.PURCHASE_EBOOK, priceId: ebookPrice.id, currency: ebookPrice.currency })
  }

  const declineEbookPurchase = () => {
    dispatch({ type: ActionType.DECLINE_EBOOK_PURCHASE })
  }

  const openRefundPolicy = () => {
    logEvent('web_openRefundPolicyEbook')
    window.open(mapRefundPolicyLink(), '_self')
  }

  const formattedDiscountedPrice = formatPriceString({
    price: formattedPrice.discountedPrice!,
    currency: formattedPrice.currency,
    withFractionDigits: !(formattedPrice.discountedPrice! >= 100)
  })

  const formattedRegularPrice = formatPriceString({
    price: formattedPrice.price,
    currency: formattedPrice.currency,
    withFractionDigits: !(formattedPrice.discountedPrice! >= 100)
  })

  const goalWeight =
    user?.personal?.goalWeight && user?.personal?.unitSystem // They should exist at this point but wanted to be defensive here
      ? getFormattedWeight(user.personal.goalWeight, user.personal.unitSystem)
      : t('ebook.targetWeightPlaceholder').toLocaleLowerCase()

  return (
    <Center>
      <MaxWidth>
        <HeaderContainer>
          <Text fontSize={theme.responsiveFontSizes.small} color={theme.colors.grey900} fontWeight='600'>
            {t('ebook.title')}
          </Text>
          <LabelContainer backgroundColor={theme.colors.green500}>
            <HeaderLabelPillText>{t('ebook.labelPill').toUpperCase()}</HeaderLabelPillText>
          </LabelContainer>
        </HeaderContainer>
        <HeaderTitleContainer>
          <Text textAlign='center' fontSize={theme.fontSizes.mediumLarge} color={theme.colors.grey700} fontWeight='700'>
            <Trans
              i18nKey= {'ebook.title2'}
              components={[<Text noWrap fontSize={theme.fontSizes.mediumLarge} color={theme.colors.primary} fontWeight='700'/>]}
              values={{ goalWeight }}
            />
          </Text>
        </HeaderTitleContainer>
        <SubHeaderContainer>
          <Text textAlign='center' marginTop={theme.responsiveSpacing.medium} fontSize={theme.fontSizes.medium} color={theme.colors.grey700} fontWeight='400' >
            <Trans
              i18nKey= {'ebook.subHeader'}
              components={[<Text fontSize={theme.fontSizes.medium} color={theme.colors.green500} fontWeight='500'/>]}
            />
          </Text>
        </SubHeaderContainer>
        <EbookListContainer>
          <EbookListItem>
            <RoundImage src={images.openBooks} alt=""/>
            <EbookListDetailsContainer>
              <DetailsTitle>
                {t('ebook.detailsTitle1')}
              </DetailsTitle>
              <Text fontSize={theme.fixedFontSizes.small} color={theme.colors.grey900} fontWeight='400'>
                {t('ebook.detailsBody1')}
              </Text>
            </EbookListDetailsContainer>
          </EbookListItem>
          <EbookListItem>
            <div style={{ position: 'relative' }}>
              <BonusLabel>
                {t('ebook.bonus')}
              </BonusLabel>
              <RoundImage src={images.healthyFoods} alt=""/>
            </div>
            <EbookListDetailsContainer>
              <DetailsTitle>
                {t('ebook.detailsTitle2')}
              </DetailsTitle>
              <Text fontSize={theme.fixedFontSizes.small} color={theme.colors.grey900} fontWeight='400'>
                {t('ebook.detailsBody2')}
              </Text>
            </EbookListDetailsContainer>
          </EbookListItem>
        </EbookListContainer>
        <PriceContainer>
          <TotalContainer>
            <TotalText>{t('total')}</TotalText>
            <TodayText>{`(${t('today').toLowerCase()})`}</TodayText>
          </TotalContainer>
          <TotalDetailsContainer>
            <TotalDetailsAmountContainer>
              <LabelContainer fontSize={theme.responsiveFontSizes.small}>{`-${t('percentNumber50')}`}</LabelContainer>
              <Text fontSize={theme.responsiveFontSizes.large} color={theme.colors.grey900} fontWeight='600'>
                {formattedDiscountedPrice}
              </Text>
            </TotalDetailsAmountContainer>
            <Text fontSize={theme.responsiveFontSizes.small} color={theme.colors.grey400} fontWeight='500'>
              {<Trans
                i18nKey= {'ebook.regularPrice'}
                components={[<RegularPriceText color={theme.colors.primary} />]}
                values={{ price: formattedRegularPrice }}
              />}
            </Text>
          </TotalDetailsContainer>
        </PriceContainer>
        <Button margin='15px 0 30px auto' primaryColor={theme.colors.primary} onClick={purchaseEbook}>{t('ebook.ctaButtonWithBonus')}</Button>
        <DisclaimerText>
          {/* By the subscription's own country, as the server routes the purchase: MoR
              buyers complete a hosted checkout, everyone else is charged on the click. */}
          {subscriptionDetails.morManaged ? t('ebook.disclaimerTextMor') : t('ebook.disclaimerText')}
        </DisclaimerText>
        <Button theme='text' onClick={declineEbookPurchase} hideLoader>{t('ebook.declineButton')}</Button>
        <BulletList>
          <li>
            {t('ebook.bulletListItem1')}
          </li>
          <li>
            {t('ebook.bulletListItem2')}
          </li>
          <li>
            {t('ebook.bulletListItem3')}
          </li>
          <li>
            {t('ebook.bulletListItem4')}
          </li>
          <li>
            {t('ebook.bulletListItem5')}
          </li>
        </BulletList>
        <Button theme='text' onClick={openRefundPolicy} hideLoader>{t('ebook.refundPolicyButton')}</Button>
      </MaxWidth>
    </Center>
  )
}

export default EbookPurchaseContainer
